// src/components/team/AddRoleModal.tsx

import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

// Updated to match the new API response structure
type PermissionResponse =
    | {
        categories?: Record<string, string[]>;  // Old structure
        [key: string]: any;
    }
    | any[];  // New structure: array of modules

type AddRoleModalProps = {
    isOpen: boolean;
    onClose: () => void;
    permissionsData: PermissionResponse | null;
    existingRoles: string[];
    onCreateRole: (roleName: string, permissions: string[]) => Promise<any>;
};

// ✅ Map permission keys to user-friendly display names
const permissionDisplayNames: Record<string, string> = {
    // Team Management
    'team.invite': 'Invite Team Members',
    'team.members.manage': 'Manage Team Members',
    'team.roles.manage': 'Manage Roles',
    'team.ownership.transfer': 'Transfer Ownership',
    'team.ownership.request': 'Request Ownership',

    // Campaign & Automation
    'campaigns.create': 'Create Campaigns',
    'campaigns.edit': 'Edit Campaigns',
    'automations.create': 'Create Automations',
    'automations.edit': 'Edit Automations',

    // Templates
    'templates.create': 'Create Templates',
    'templates.view': 'View Templates',
    'templates.delete': 'Delete Templates',
    'templates.duplicate': 'Duplicate Templates',
    'templates.send_test': 'Send Test Emails',
};

// ✅ Get display name for a permission
const getPermissionDisplayName = (permission: string): string => {
    return permissionDisplayNames[permission] || permission;
};

const AddRoleModal: React.FC<AddRoleModalProps> = ({
    isOpen,
    onClose,
    permissionsData,
    existingRoles,
    onCreateRole,
}) => {
    const [roleName, setRoleName] = useState('');
    const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
    const [roleNameError, setRoleNameError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reset form when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setRoleName('');
            setSelectedPermissions(new Set());
            setRoleNameError('');
            setIsSubmitting(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handlePermissionToggle = (permission: string) => {
        const newSelected = new Set(selectedPermissions);
        if (newSelected.has(permission)) {
            newSelected.delete(permission);
        } else {
            newSelected.add(permission);
        }
        setSelectedPermissions(newSelected);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate role name
        const trimmedName = roleName.trim();
        if (!trimmedName) {
            setRoleNameError('Role name is required');
            return;
        }

        // Check if role already exists
        if (existingRoles.some(role => role.toLowerCase() === trimmedName.toLowerCase())) {
            setRoleNameError('A role with this name already exists');
            return;
        }

        setRoleNameError('');
        setIsSubmitting(true);

        try {
            const permissionsArray = Array.from(selectedPermissions);
            await onCreateRole(trimmedName, permissionsArray);
            onClose();
        } catch (error) {
            // Error is already handled in the parent
            // console.error('Error creating role:', error);
            toast.error('Failed to create role. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // UPDATED: Get all permissions as a flat array with category info
    const getAllPermissions = (): { category: string; permission: string }[] => {
        if (!permissionsData) return [];

        const result: { category: string; permission: string }[] = [];

        // Check if it's the new structure (array of modules)
        if (Array.isArray(permissionsData)) {
            permissionsData.forEach((module) => {
                const category = module.module || 'Other';
                if (module.permissions && Array.isArray(module.permissions)) {
                    module.permissions.forEach((perm: any) => {
                        if (perm.code) {
                            result.push({ category, permission: perm.code });
                        }
                    });
                }
            });
        }
        // Check if it's the old structure (with categories)
        else if (permissionsData && typeof permissionsData === 'object' && 'categories' in permissionsData) {
            const categories = permissionsData.categories;
            if (categories && typeof categories === 'object') {
                Object.entries(categories).forEach(([category, permissions]) => {
                    if (Array.isArray(permissions)) {
                        permissions.forEach((permission) => {
                            result.push({ category, permission });
                        });
                    }
                });
            }
        }
        // Fallback: try to extract from any structure
        else {
            console.warn('Unknown permissions data structure in AddRoleModal:', permissionsData);
            // Try to extract from object values
            if (permissionsData && typeof permissionsData === 'object') {
                Object.values(permissionsData).forEach((value) => {
                    if (Array.isArray(value)) {
                        value.forEach((item) => {
                            if (typeof item === 'string') {
                                result.push({ category: 'Other', permission: item });
                            } else if (item && typeof item === 'object' && item.code) {
                                result.push({ category: item.module || 'Other', permission: item.code });
                            }
                        });
                    }
                });
            }
        }

        return result;
    };

    const allPermissions = getAllPermissions();

    // Get category display name (clean up the category name)
    const getCategoryDisplayName = (category: string): string => {
        // Capitalize first letter and replace underscores with spaces
        return category
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Count selected permissions
    const selectedCount = selectedPermissions.size;

    return (
        <div
            className="fixed inset-0 z-[99999] flex items-center justify-center"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-800">Create New Role</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        disabled={isSubmitting}
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    <form onSubmit={handleSubmit} id="add-role-form">
                        {/* Role Name */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Role Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={roleName}
                                onChange={(e) => {
                                    setRoleName(e.target.value);
                                    if (roleNameError) setRoleNameError('');
                                }}
                                placeholder="Enter role name (e.g., 'Manager')"
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#00A4BD] ${roleNameError ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                disabled={isSubmitting}
                            />
                            {roleNameError && (
                                <p className="mt-1 text-sm text-red-500">{roleNameError}</p>
                            )}
                        </div>

                        {/* Permissions */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <label className="block text-sm font-medium text-gray-700">
                                    Permissions <span className="text-red-500">*</span>
                                </label>
                                <span className="text-xs text-gray-500">
                                    {selectedCount} selected
                                </span>
                            </div>
                            <div className="space-y-4 max-h-[300px] overflow-y-auto border border-gray-200 rounded-md p-3">
                                {allPermissions.length === 0 ? (
                                    <p className="text-gray-500 text-sm">No permissions available</p>
                                ) : (
                                    allPermissions.map(({ category, permission }, index) => {
                                        const isFirstInCategory = index === 0 || allPermissions[index - 1]?.category !== category;
                                        const displayName = getPermissionDisplayName(permission);

                                        return (
                                            <div key={`${category}-${permission}-${index}`}>
                                                {isFirstInCategory && (
                                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                                        {getCategoryDisplayName(category)}
                                                    </p>
                                                )}
                                                <label className="flex items-center space-x-3 py-1.5 hover:bg-gray-50 rounded px-2 cursor-pointer transition-colors">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedPermissions.has(permission)}
                                                        onChange={() => handlePermissionToggle(permission)}
                                                        disabled={isSubmitting}
                                                        className="w-4 h-4 rounded border-gray-300 text-[#00A4BD] focus:ring-[#00A4BD] cursor-pointer disabled:opacity-50"
                                                    />
                                                    <span className="text-sm text-gray-700">{displayName}</span>
                                                </label>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </form>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="add-role-form"
                        disabled={isSubmitting || !roleName.trim() || selectedPermissions.size === 0}
                        className={`px-4 py-2 rounded-md transition-colors ${isSubmitting || !roleName.trim() || selectedPermissions.size === 0
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-[#00A4BD] text-white hover:bg-[#00899E]'
                            }`}
                    >
                        {isSubmitting ? 'Creating...' : 'Create Role'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddRoleModal;